export type FeedbackVote = 'upvote' | 'downvote' | 'star';
export type DownvoteReason = 'not_relevant' | 'low_quality' | null;
export type FeedbackIcon = 'thumbs-up' | 'thumbs-down' | 'star';
export type ProjectFeedRelevanceFilter = 'all' | 'must-read' | 'related';
export type ProjectFeedFeedbackFilter =
    | 'all'
    | 'unrated'
    | 'liked'
    | 'disliked'
    | 'starred'
    | 'not-relevant'
    | 'low-quality';

export interface ProjectPaperFeedback {
    vote: FeedbackVote;
    downvote_reason: DownvoteReason;
    updated_at: string;
}

export interface FeedbackFilterOption {
    value: ProjectFeedFeedbackFilter;
    label: MessageDescriptor;
    icon?: FeedbackIcon;
    activeTone?: 'default' | 'must-read' | 'liked' | 'starred' | 'disliked';
}

export const PROJECT_FEED_FEEDBACK_FILTER_OPTIONS: FeedbackFilterOption[] = [
    { value: 'all', label: msg({ id: 'feedback.filter.all', message: 'All' }), activeTone: 'default' },
    { value: 'unrated', label: msg({ id: 'feedback.filter.none', message: 'No feedback' }), activeTone: 'default' },
    { value: 'liked', label: msg({ id: 'feedback.useful', message: 'Useful' }), icon: 'thumbs-up', activeTone: 'liked' },
    { value: 'starred', label: msg({ id: 'feedback.veryUseful', message: 'Very useful' }), icon: 'star', activeTone: 'starred' },
    { value: 'disliked', label: msg({ id: 'feedback.notForMe', message: 'Not for me' }), icon: 'thumbs-down', activeTone: 'disliked' },
];

export function getFeedbackVoteLabel(vote: FeedbackVote): MessageDescriptor {
    if (vote === 'upvote') {
        return msg({ id: 'feedback.useful', message: 'Useful' });
    }

    if (vote === 'star') {
        return msg({ id: 'feedback.veryUseful', message: 'Very useful' });
    }

    return msg({ id: 'feedback.notForMe', message: 'Not for me' });
}

export function getFeedbackVoteIcon(vote: FeedbackVote): FeedbackIcon {
    if (vote === 'upvote') {
        return 'thumbs-up';
    }

    if (vote === 'star') {
        return 'star';
    }

    return 'thumbs-down';
}

export function isProjectFeedRelevanceFilter(value: unknown): value is ProjectFeedRelevanceFilter {
    return value === 'all' || value === 'must-read' || value === 'related';
}

export function isProjectFeedFeedbackFilter(value: unknown): value is ProjectFeedFeedbackFilter {
    return value === 'all'
        || value === 'unrated'
        || value === 'liked'
        || value === 'disliked'
        || value === 'starred'
        || value === 'not-relevant'
        || value === 'low-quality';
}

export function matchesFeedbackFilter(
    feedback: ProjectPaperFeedback | null | undefined,
    feedbackFilter: ProjectFeedFeedbackFilter
): boolean {
    if (feedbackFilter === 'all') {
        return true;
    }

    if (feedbackFilter === 'unrated') {
        return !feedback;
    }

    if (!feedback) {
        return false;
    }

    if (feedbackFilter === 'liked') {
        return feedback.vote === 'upvote';
    }

    if (feedbackFilter === 'disliked') {
        return feedback.vote === 'downvote';
    }

    if (feedbackFilter === 'starred') {
        return feedback.vote === 'star';
    }

    if (feedbackFilter === 'not-relevant') {
        return feedback.vote === 'downvote' && feedback.downvote_reason === 'not_relevant';
    }

    return feedback.vote === 'downvote' && feedback.downvote_reason === 'low_quality';
}
import type { MessageDescriptor } from '@lingui/core';
import { msg } from '@lingui/core/macro';
